import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { HiOutlineDocumentText } from "react-icons/hi";
import { IoLocationOutline } from "react-icons/io5";
import { RiContactsLine } from "react-icons/ri";
import { AiOutlineAudio } from "react-icons/ai";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { updateNavigateToSpesificMessage } from "../../../../store/Slices/NavigateToSpesificMessageSlice";
import { useTheme } from "../../../../context/ThemeProvider";
import { Message } from "../../../../types/MessageListType";
import ShowSavedName from "../../../../components/ShowSavedName";
import { getRandomColor } from "../../../../utils/getRandomColor";

export default function PinMessages() {
  const pinMessageData = useAppSelector(
    (state) => state.MessageList.pinned_messages.Records,
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (pinMessageData.length === 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((prev) => Math.min(prev, pinMessageData.length - 1));
  }, [pinMessageData.length]);

  const activeMessage = pinMessageData[activeIndex];
  const hasMultiple = pinMessageData.length > 1;

  function goPrev(e: MouseEvent) {
    e.stopPropagation();
    if (!hasMultiple) return;
    setActiveIndex((prev) =>
      prev === 0 ? pinMessageData.length - 1 : prev - 1,
    );
  }

  function goNext(e: MouseEvent) {
    e.stopPropagation();
    if (!hasMultiple) return;
    setActiveIndex((prev) =>
      prev === pinMessageData.length - 1 ? 0 : prev + 1,
    );
  }

  function navigateToThatChat(messageData: Message) {
    dispatch(
      updateNavigateToSpesificMessage({
        chat_id: messageData.chat_id,
        navigate_to_message: true,
        message_id: messageData.message_id,
      }),
    );
  }

  if (!activeMessage) return null;

  return (
    <div
      data-pin-bar
      className="absolute z-30 flex w-full select-none flex-col items-center justify-center"
    >
      <div className="flex h-[52px] w-full items-center gap-2.5 bg-pinMessageListHeader px-3">
        <PinStrip count={pinMessageData.length} activeIndex={activeIndex} />

        <button
          type="button"
          onClick={() => navigateToThatChat(activeMessage)}
          className="h-full min-w-0 flex-1 py-1.5 text-left"
        >
          <PinPreview messageData={activeMessage} />
        </button>

        {hasMultiple && (
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              aria-label="Previous pinned message"
              onClick={goPrev}
              className="grid h-8 w-8 place-content-center rounded-md text-darkText/80 transition hover:bg-black/5 dark:hover:bg-white/10"
            >
              <FaChevronUp className="text-base" />
            </button>
            <button
              type="button"
              aria-label="Next pinned message"
              onClick={goNext}
              className="grid h-8 w-8 place-content-center rounded-md text-darkText/80 transition hover:bg-black/5 dark:hover:bg-white/10"
            >
              <FaChevronDown className="text-base" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PinStrip({
  count,
  activeIndex,
}: {
  count: number;
  activeIndex: number;
}) {
  // Fits inside fixed 52px bar; grows a bit with pin count, then dashes
  const height = Math.min(18 + count * 4, 36);
  const dashed = count > 5;

  if (dashed) {
    const markerTop =
      count <= 1 ? 0 : (activeIndex / (count - 1)) * (height - 8);

    return (
      <div
        className="relative my-auto w-[3px] shrink-0 overflow-hidden rounded-full"
        style={{ height }}
        aria-hidden
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, var(--theme-color) 0 2px, transparent 2px 5px)",
            opacity: 0.45,
          }}
        />
        <div
          className="absolute left-0 w-full rounded-full bg-themeColor transition-all duration-300"
          style={{ top: markerTop, height: 8 }}
        />
      </div>
    );
  }

  return (
    <div
      className="my-auto flex w-[3px] shrink-0 flex-col gap-[2px] transition-all duration-300"
      style={{ height }}
      aria-hidden
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="min-h-[2px] flex-1 rounded-full bg-themeColor transition-all duration-300"
          style={{ opacity: i === activeIndex ? 1 : 0.28 }}
        />
      ))}
    </div>
  );
}

function PinPreview({ messageData }: { messageData: Message }) {
  // @ts-ignore
  const { theme } = useTheme();
  const userData = useAppSelector((state) => state.userData);
  const isOwn =
    (messageData.User?.user_id ?? messageData.sender_id) == userData?.user_id;
  const preview = useMemo(
    () => getPinPreviewText(messageData),
    [messageData.message_id, messageData.message_type, messageData.message_content, messageData.message_thumbnail],
  );

  return (
    <div className="flex h-full min-w-0 flex-col justify-center">
      <div
        style={{
          color: isOwn
            ? undefined
            : getRandomColor(messageData.User?.user_name || "user", theme),
        }}
        className={`h-5 truncate text-sm font-medium leading-5 [&>div]:truncate ${isOwn ? "text-darkText" : ""}`}
      >
        {isOwn ? (
          userData?.full_name || userData?.user_name || "You"
        ) : messageData.User ? (
          <ShowSavedName
            user_id={String(messageData.User.user_id)}
            name={messageData.User.full_name}
          />
        ) : (
          "User"
        )}
      </div>
      <div className="mt-0.5 h-5 overflow-hidden text-xs leading-5 text-lightText">
        <PinPreviewBody messageData={messageData} preview={preview} />
      </div>
    </div>
  );
}

function PinPreviewBody({
  messageData,
  preview,
}: {
  messageData: Message;
  preview: string;
}) {
  if (
    messageData.message_type === "image" ||
    messageData.message_type === "gif"
  ) {
    return (
      <span className="flex h-5 items-center gap-1.5">
        <img
          src={messageData.message_content}
          alt=""
          className="h-5 w-5 shrink-0 rounded object-cover"
        />
        <span className="truncate">{preview}</span>
      </span>
    );
  }

  if (messageData.message_type === "video") {
    return (
      <span className="flex h-5 items-center gap-1.5">
        <img
          src={messageData.message_thumbnail}
          alt=""
          className="h-5 w-5 shrink-0 rounded object-cover"
        />
        <span className="truncate">{preview}</span>
      </span>
    );
  }

  if (messageData.message_type === "audio") {
    return (
      <span className="inline-flex h-5 items-center gap-1 truncate">
        <AiOutlineAudio className="shrink-0 text-sm" />
        {preview}
      </span>
    );
  }

  if (messageData.message_type === "voice") {
    return (
      <span className="inline-flex h-5 items-center gap-1 truncate">
        <AiOutlineAudio className="shrink-0 text-sm" />
        {preview}
      </span>
    );
  }

  if (messageData.message_type === "doc") {
    return (
      <span className="inline-flex h-5 items-center gap-1 truncate">
        <HiOutlineDocumentText className="shrink-0 text-sm" />
        {preview}
      </span>
    );
  }

  if (messageData.message_type === "location") {
    return (
      <span className="inline-flex h-5 items-center gap-1 truncate">
        <IoLocationOutline className="shrink-0 text-sm" />
        {preview}
      </span>
    );
  }

  if (messageData.message_type === "contact") {
    return (
      <span className="inline-flex h-5 items-center gap-1 truncate">
        <RiContactsLine className="shrink-0 text-sm" />
        {preview}
      </span>
    );
  }

  if (messageData.message_type === "link") {
    return <span className="block truncate">🔗 {preview}</span>;
  }

  return <span className="block truncate">{preview}</span>;
}

function getPinPreviewText(messageData: Message): string {
  switch (messageData.message_type) {
    case "image":
      return "Photo";
    case "gif":
      return "GIF";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "voice":
      return "Voice message";
    case "doc":
      return "Document";
    case "location":
      return "Location";
    case "contact":
      return "Contact";
    case "link":
    case "text":
    default:
      return messageData.message_content || "";
  }
}
